import { Injectable } from '@angular/core';
import { ProcessErrorCode } from 'src/app/ajs-upgraded-providers';
import { TrackerService } from 'src/app/components/logging/tracker.service';
import { ModalService } from 'src/app/components/modals/modal.service';
import { DisplayApiService } from 'src/app/api/services/display-api.service';
import { DisplayService } from './display.service';

@Injectable({
  providedIn: 'root'
})
export class PlayerActionsService {
  controlsError: string = '';

  constructor(
    private displayApiService: DisplayApiService,
    private modalService: ModalService,
    private processErrorCode: ProcessErrorCode,
    private trackerService: TrackerService,
    private displayService: DisplayService
  ) { }

  _restart (displayId, displayName) {
    if (!displayId) {
      return;
    }

    return this.displayApiService.restart(displayId)
      .then(() => {
        this.trackerService.displayEvent('Display Restarted', displayId, displayName);
      });
  }

  private _reboot (displayId, displayName) {
    if (!displayId) {
      return;
    }

    return this.displayApiService.reboot(displayId)
      .then(() => {
        this.trackerService.displayEvent('Display Rebooted', displayId, displayName);
      });
  }

  private _updateFirmware (displayId, displayName, firmwareUrl?) {
    if (!displayId) {
      return;
    }

    // Send firmware update request to backend and handle response status
    // The backend handles the actual firmware update process
    // We only need to send the request and handle the status response

    return this.displayApiService.updateFirmware(displayId, firmwareUrl)
        .then((response) => {
          // Handle different status responses from the backend
          // Guard against missing response (e.g., 204 No Content responses)
          let status;
          if (response && (response as any).item) {
            status = (response.item as any)?.firmwareUpdateStatus;
          }

          if (status === 'pending') {
            this.trackerService.displayEvent('Display Firmware Update Requested', displayId, displayName);
          } else if (status === 'complete') {
            this.trackerService.displayEvent('Display Firmware Update Complete', displayId, displayName);
          } else if (status === 'failed') {
            this.trackerService.displayEvent('Display Firmware Update Failed', displayId, displayName);
          } else if (status === 'not_available') {
            this.trackerService.displayEvent('Display Firmware Update Not Available', displayId, displayName);
          } else {
            // Default case - assume request was sent successfully (handles undefined status and 204 responses)
            this.trackerService.displayEvent('Display Firmware Update Requested', displayId, displayName);
          }

          return response;
        })
      .catch((error) => {
        // Handle API request failures
        this.trackerService.displayEvent('Display Firmware Update Request Failed', displayId, displayName);
        throw error;
      });
  }

  restartByObject (display) {
    return this._restart(display.id, display.name);
  }

  rebootByObject (display) {
    return this._reboot(display.id, display.name);
  }

  updateFirmwareByObject (display, firmwareUrl?) {
    return this._updateFirmware(display.id, display.name, firmwareUrl);
  }

  private _confirmRestart (displayId, displayName) {
    this.modalService.confirm('Restart Rise Player?',
      'Rise Player will restart and the content showing on your display will be interrupted. Do you wish to proceed?',
      'Yes',
      'No'
    ).then(() => {
      // do what you need if user presses ok
      this.controlsError = '';

      this._restart(displayId, displayName).catch((e) => {
        this.controlsError = this.processErrorCode(e);
      });
    }).catch(() => {
      // do what you need to do if user cancels
    });
  }

  private _confirmReboot (displayId, displayName) {
    this.modalService.confirm('Reboot media player?',
      'The media player will reboot and the content showing on your display will be interrupted. Do you wish to proceed?',
      'Yes',
      'No'
    ).then(() => {
      // do what you need if user presses ok
      this.controlsError = '';

      this._reboot(displayId, displayName).catch((e) => {
        this.controlsError = this.processErrorCode(e);
      });
    }).catch(() => {
      // do what you need to do if user cancels
    });
  }

  private _confirmUpdateFirmware (displayId, displayName) {
    this.modalService.showFirmwareUpdateModal({
      displayName: displayName,
      isMultiple: false
    }).then((result) => {
      if (result && result.firmwareUrl) {
        // User provided firmware URL - proceed with firmware update
        this.controlsError = '';

        const updatePromise = this._updateFirmware(displayId, displayName, result.firmwareUrl);
        if (updatePromise) {
          updatePromise.catch((e) => {
            this.controlsError = this.processErrorCode(e);
          });
        }
      }
      // User cancelled - do nothing
    }).catch(() => {
      // Modal error - do nothing
    });
  }

  confirm (displayId, displayName, mode) {
    if (this.displayService.showLicenseRequired()) {
      return;
    }

    if (mode === 'reboot') {
      this._confirmReboot(displayId, displayName);
    } else if (mode === 'restart') {
      this._confirmRestart(displayId, displayName);
    } else if (mode === 'updateFirmware') {
      this._confirmUpdateFirmware(displayId, displayName);
    }
  }
}
